import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { getCustomerUniqueKey, toUniqueCustomers } from "../../../utils/customerUtils";

// is_trialed の緩い判定（MySQLのTINYINTが1/0で返る場合も対応）
const isTrialed = (c) => c.is_trialed === true || c.is_trialed === 1 || c.is_trialed === "true";

// 全分析共通フィルター（is_trialed → 期間 → 店舗 → レッスン → 性別の順で適用）
function applyFilters(customers, { filterTrialDateStart, filterTrialDateEnd, filterStudio, filterLesson, selectedGenders }) {
  let result = customers.filter(isTrialed);

  if (filterTrialDateStart) {
    // リストページと同じ文字列比較（UTC変換なし）
    result = result.filter((c) => c.trial_date && c.trial_date >= filterTrialDateStart);
  }
  if (filterTrialDateEnd) {
    // 終了日は23:59:59まで含める（YYYY-MM-DD 形式なので文字列 <= で当日を含む）
    result = result.filter((c) => c.trial_date && c.trial_date <= filterTrialDateEnd);
  }
  if (filterStudio) {
    result = result.filter((c) => String(c.studio_id) === String(filterStudio));
  }
  if (filterLesson) {
    // "::" を含む場合は studio_id::lesson_name 形式、それ以外は lesson_id
    if (filterLesson.includes("::")) {
      const sepIdx = filterLesson.indexOf("::");
      const sid = filterLesson.slice(0, sepIdx);
      const lname = filterLesson.slice(sepIdx + 2);
      result = result.filter((c) => String(c.studio_id) === sid && c.lesson_name === lname);
    } else {
      result = result.filter((c) => String(c.lesson_id) === filterLesson);
    }
  }
  const allGenders = ["男性", "女性", "その他"];
  const isAllSelected = allGenders.every((g) => selectedGenders.includes(g));
  if (selectedGenders.length > 0 && !isAllSelected) {
    result = result.filter((c) => selectedGenders.includes(c.gender));
  }
  return result;
}

// 分析カード数値を算出
// trialCount: ユニーク顧客数、bookingCount: 体験済み予約件数、memberCount/conversionRate: ユニークベース
function computeAnalytics(filteredCustomers) {
  const bookingCount = filteredCustomers.length;
  const unique = toUniqueCustomers(filteredCustomers);
  const trialCount = unique.length;
  const memberCount = unique.filter((c) => c.is_member).length;
  const conversionRate = trialCount > 0 ? Math.round((memberCount / trialCount) * 1000) / 10 : 0;
  return { trialCount, bookingCount, memberCount, conversionRate };
}

// 月次推移グラフデータ（月内ユニーク顧客でカウント）
function computeTrendData(customers, filterTrialDateStart, filterTrialDateEnd) {
  const monthlyData = {};
  const uniqueByMonth = {};
  const useDateRange = !!(filterTrialDateStart && filterTrialDateEnd);

  if (useDateRange) {
    const cur = new Date(filterTrialDateStart);
    const endDate = new Date(filterTrialDateEnd);
    while (cur <= endDate) {
      const year = cur.getFullYear();
      const month = cur.getMonth() + 1;
      const key = `${year}-${String(month).padStart(2, "0")}`;
      monthlyData[key] = { year, month, monthLabel: `${year}年${month}月`, 体験者: 0, 入会者: 0 };
      cur.setMonth(cur.getMonth() + 1);
    }
  }

  for (const c of customers) {
    if (!c.trial_date) continue;
    const d = new Date(c.trial_date);
    const year = d.getFullYear();
    const month = d.getMonth() + 1;
    const key = `${year}-${String(month).padStart(2, "0")}`;

    if (!monthlyData[key]) {
      monthlyData[key] = { year, month, monthLabel: `${year}年${month}月`, 体験者: 0, 入会者: 0 };
    }

    const ckey = getCustomerUniqueKey(c);
    if (!uniqueByMonth[key]) uniqueByMonth[key] = new Set();
    if (uniqueByMonth[key].has(ckey)) continue;
    uniqueByMonth[key].add(ckey);

    monthlyData[key].体験者 += 1;
    if (c.is_member) monthlyData[key].入会者 += 1;
  }

  return Object.values(monthlyData)
    .sort((a, b) => (a.year !== b.year ? a.year - b.year : a.month - b.month))
    .map(({ monthLabel, 体験者, 入会者 }) => ({ month: monthLabel, 体験者, 入会者 }));
}

// 来店きっかけ円グラフデータ
function computeReasonData(customers) {
  const counts = {};
  for (const c of customers) {
    if (c.visit_reason) {
      const r = c.visit_reason.trim();
      if (r) counts[r] = (counts[r] || 0) + 1;
    }
  }
  return Object.entries(counts)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
}

// 店舗・レッスン別集計（booking単位カウントで studio合計 = レッスン合計が成立）
function buildStudioBreakdown(customers) {
  const studioMap = {};
  for (const c of customers) {
    const sid = String(c.studio_id || "unknown");
    const sname = c.studio_name || sid;
    const lkey = c.lesson_id ? String(c.lesson_id) : `${sid}::${c.lesson_name || ""}`;
    const lname = c.lesson_name || "（未設定）";

    if (!studioMap[sid]) {
      studioMap[sid] = { studioId: sid, studioName: sname, count: 0, memberCount: 0, lessons: {} };
    }
    studioMap[sid].count += 1;
    if (c.is_member) studioMap[sid].memberCount += 1;

    if (!studioMap[sid].lessons[lkey]) {
      studioMap[sid].lessons[lkey] = { lessonKey: lkey, lessonName: lname, count: 0, memberCount: 0 };
    }
    studioMap[sid].lessons[lkey].count += 1;
    if (c.is_member) studioMap[sid].lessons[lkey].memberCount += 1;
  }

  return Object.values(studioMap).map((studio) => {
    const lessons = Object.values(studio.lessons).map((l) => ({
      lessonKey: l.lessonKey,
      lessonName: l.lessonName,
      trialCount: l.count,
      memberCount: l.memberCount,
      conversionRate: l.count > 0 ? Math.round((l.memberCount / l.count) * 1000) / 10 : 0,
    }));

    const lessonTotal = lessons.reduce((s, l) => s + l.trialCount, 0);
    if (lessonTotal !== studio.count) {
      console.warn(
        `[体験数不一致] ${studio.studioName}: 店舗体験数=${studio.count}, レッスン体験数合計=${lessonTotal}`,
      );
    }

    return {
      studioId: studio.studioId,
      studioName: studio.studioName,
      trialCount: studio.count,
      memberCount: studio.memberCount,
      conversionRate: studio.count > 0 ? Math.round((studio.memberCount / studio.count) * 1000) / 10 : 0,
      lessons,
    };
  });
}

const DAYS_EN = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
const DAY_JA = { monday: "月", tuesday: "火", wednesday: "水", thursday: "木", friday: "金", saturday: "土", sunday: "日" };
const DAY_ORDER = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

function getDayFromDateStr(dateStr) {
  if (!dateStr) return "monday";
  const [year, month, day] = dateStr.split("-").map(Number);
  return DAYS_EN[new Date(year, month - 1, day).getDay()];
}

// allCustomers から実際に体験予約が存在するレッスンのみ抽出
// lessonフィルターは除外し、他の条件（is_trialed・期間・店舗・性別）は適用する
function computeAvailableLessons(customers, { filterTrialDateStart, filterTrialDateEnd, filterStudio, selectedGenders }) {
  let filtered = customers.filter(isTrialed);

  if (filterTrialDateStart) {
    filtered = filtered.filter((c) => c.trial_date && c.trial_date >= filterTrialDateStart);
  }
  if (filterTrialDateEnd) {
    filtered = filtered.filter((c) => c.trial_date && c.trial_date <= filterTrialDateEnd);
  }
  if (filterStudio) {
    filtered = filtered.filter((c) => String(c.studio_id) === String(filterStudio));
  }
  const allGenders = ["男性", "女性", "その他"];
  const isAllSelected = allGenders.every((g) => selectedGenders.includes(g));
  if (selectedGenders.length > 0 && !isAllSelected) {
    filtered = filtered.filter((c) => selectedGenders.includes(c.gender));
  }

  // lesson_id があれば優先、なければ studio_id::lesson_name で一意識別
  const lessonMap = {};
  for (const c of filtered) {
    const lkey = c.lesson_id ? String(c.lesson_id) : `${c.studio_id}::${c.lesson_name || ""}`;
    if (!lessonMap[lkey]) {
      const day = getDayFromDateStr(c.trial_date);
      lessonMap[lkey] = {
        lesson_id: c.lesson_id,
        name: c.lesson_name || "",
        day,
        day_ja: DAY_JA[day],
        studio_id: c.studio_id,
        studio_name: c.studio_name || "",
        count: 0,
      };
    }
    lessonMap[lkey].count += 1;
  }

  const lessons = Object.values(lessonMap).sort(
    (a, b) => DAY_ORDER.indexOf(a.day) - DAY_ORDER.indexOf(b.day),
  );

  console.log(
    "[レッスン選択肢]",
    lessons.map((l) => `${l.studio_name}/${l.name}: ${l.count}件`),
  );

  return lessons;
}

export default function AnalyticsPage() {
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [filterTrialDateStart, setFilterTrialDateStart] = useState("");
  const [filterTrialDateEnd, setFilterTrialDateEnd] = useState("");
  const [filterStudio, setFilterStudio] = useState("");
  const [filterLesson, setFilterLesson] = useState("");
  const [selectedGenders, setSelectedGenders] = useState([]);
  const [studiosList, setStudiosList] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchStudios = useCallback(async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const res = await fetch(`${apiUrl}/api/get_studios_list/`);
      if (!res.ok) throw new Error("ネットワークエラー");
      const data = await res.json();
      setStudiosList(data.studios || []);
    } catch {
      setStudiosList([]);
    }
  }, []);

  // 全体験者データを一括取得（クライアント側で絞り込む）
  const fetchAllCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const res = await fetch(`${apiUrl}/api/get_trial_customers/?page_size=10000`);
      if (!res.ok) throw new Error("ネットワークエラー");
      const data = await res.json();
      const customers = data.trial_customers || [];
      setAllCustomers(customers);
      console.log(`[Analytics] 1. API取得全件数: ${customers.length}`);
      console.log(`[Analytics] 2. is_trialed フィルター後: ${customers.filter(isTrialed).length}`);
    } catch {
      setAllCustomers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStudios();
    fetchAllCustomers();
  }, [fetchStudios, fetchAllCustomers]);

  // レッスン一覧を allCustomers から生成（体験予約0件のレッスンを除外）
  // lessonフィルター自身は除外し、他のフィルターは適用する
  const lessonsList = useMemo(
    () => computeAvailableLessons(allCustomers, { filterTrialDateStart, filterTrialDateEnd, filterStudio, selectedGenders }),
    [allCustomers, filterTrialDateStart, filterTrialDateEnd, filterStudio, selectedGenders],
  );

  // 共通フィルターを適用した顧客リスト（全分析の single source of truth）
  const filteredCustomers = useMemo(
    () =>
      applyFilters(allCustomers, {
        filterTrialDateStart,
        filterTrialDateEnd,
        filterStudio,
        filterLesson,
        selectedGenders,
      }),
    [allCustomers, filterTrialDateStart, filterTrialDateEnd, filterStudio, filterLesson, selectedGenders],
  );

  const analyticsData = useMemo(
    () => computeAnalytics(filteredCustomers),
    [filteredCustomers],
  );

  const trendData = useMemo(
    () => computeTrendData(filteredCustomers, filterTrialDateStart, filterTrialDateEnd),
    [filteredCustomers, filterTrialDateStart, filterTrialDateEnd],
  );

  const reasonData = useMemo(() => computeReasonData(filteredCustomers), [filteredCustomers]);

  // 店舗・レッスン別集計（整合性チェックのみ。console.warn で差分確認）
  useEffect(() => {
    buildStudioBreakdown(filteredCustomers);
  }, [filteredCustomers]);

  // 集計デバッグログ（リストページとの突き合わせ確認用）
  useEffect(() => {
    if (allCustomers.length === 0) return;

    const bookingCount = filteredCustomers.length;
    const uniqueCustomers = toUniqueCustomers(filteredCustomers);
    const trialCount = uniqueCustomers.length;

    console.log(`[Analytics] ── 集計確認 ──────────────────────`);
    console.log(`[Analytics] 体験数（filteredCustomers.length）: ${bookingCount}`);
    console.log(`[Analytics] 体験者数（toUniqueCustomers.length）: ${trialCount}`);
    console.log(`[Analytics] 差分（重複して体験した人数）: ${bookingCount - trialCount}`);

    // 同一キーにまとめられているグループを表示
    const keyCounts = {};
    for (const c of filteredCustomers) {
      const key = getCustomerUniqueKey(c);
      keyCounts[key] = (keyCounts[key] || 0) + 1;
    }
    const duplicates = Object.entries(keyCounts).filter(([, v]) => v > 1);
    if (duplicates.length > 0) {
      console.log(`[Analytics] 複数予約まとめ対象（${duplicates.length}グループ）:`);
      for (const [key, count] of duplicates) {
        const group = filteredCustomers.filter((c) => getCustomerUniqueKey(c) === key);
        console.log(`  キー "${key}" (${count}件):`, group.map((c) => `ID:${c.id} ${c.last_name}${c.first_name}`).join(", "));
      }
    }

    // mail・phone 未設定で fallback になったレコード数
    const fallbackCount = filteredCustomers.filter(
      (c) => !(c.mail || "").trim() && !(c.phone_number || "").trim(),
    ).length;
    if (fallbackCount > 0) {
      console.warn(`[Analytics] mail・phone 未設定（fallback booking-id）: ${fallbackCount}件 → 体験者数が実際より多い可能性あり`);
    }
    console.log(`[Analytics] ────────────────────────────────`);
  }, [allCustomers, filteredCustomers]);

  const handleReset = () => {
    setFilterTrialDateStart("");
    setFilterTrialDateEnd("");
    setFilterStudio("");
    setFilterLesson("");
    setSelectedGenders([]);
  };

  const handleGenderToggle = (gender) => {
    setSelectedGenders((prev) =>
      prev.includes(gender) ? prev.filter((g) => g !== gender) : [...prev, gender],
    );
  };

  // レッスンオプションのvalue: lesson_id があれば使用、なければ studio_id::lesson_name
  const getLessonOptionValue = (lesson) => {
    if (lesson.lesson_id) return String(lesson.lesson_id);
    return `${lesson.studio_id}::${lesson.name}`;
  };

  const COLORS = [
    "#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#A28BFF",
    "#FF6699", "#FF4444", "#AAAAAA", "#2ECC40", "#FFDD57",
  ];

  const contentStyle = { backgroundColor: "#00000090", padding: "0.5rem 2rem" };
  const labelStyle = { fontSize: 12 };

  return (
    <div>
      <div className="filterSectionWrap">
        <div className="filterSectionInner">
          <div className="buttonWrap">
            <button className="filter" onClick={() => setIsFilterOpen(!isFilterOpen)}>
              フィルター・絞り込み
            </button>
            <button className="sort-reset" onClick={handleReset}>
              リセット
            </button>
          </div>

          <div className={`sort-setting ${isFilterOpen ? "on" : ""}`}>
            <dl>
          <dt>期間</dt>
          <dd>
            <input
              type="date"
              value={filterTrialDateStart}
              onChange={(e) => setFilterTrialDateStart(e.target.value)}
            />
            <span>〜</span>
            <input
              type="date"
              value={filterTrialDateEnd}
              onChange={(e) => setFilterTrialDateEnd(e.target.value)}
            />
          </dd>
        </dl>
        <dl>
          <dt>店舗</dt>
          <dd>
            <select
              value={filterStudio}
              onChange={(e) => {
                setFilterStudio(e.target.value);
                setFilterLesson("");
              }}
            >
              <option value="">すべて</option>
              {studiosList.map((studio) => (
                <option key={studio.id} value={studio.id}>
                  {studio.name}
                </option>
              ))}
            </select>
          </dd>
        </dl>
        <dl>
          <dt>性別</dt>
          <dd>
            {["男性", "女性", "その他"].map((gender) => (
              <button
                key={gender}
                className={selectedGenders.includes(gender) ? "on" : ""}
                onClick={() => handleGenderToggle(gender)}
              >
                {gender}
              </button>
            ))}
          </dd>
        </dl>
        <dl>
          <dt>レッスン</dt>
          <dd>
            <select value={filterLesson} onChange={(e) => setFilterLesson(e.target.value)}>
              <option value="">すべて</option>
              {["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"].map((day) => {
                const dayLessons = lessonsList.filter((l) => l.day === day);
                if (dayLessons.length === 0) return null;
                const dayJa = dayLessons[0].day_ja;
                return (
                  <optgroup key={day} label={`${dayJa}曜日`}>
                    {dayLessons.map((lesson) => {
                      const val = getLessonOptionValue(lesson);
                      // 店舗未選択時は店舗名を付加して同名レッスンを区別
                      const label = filterStudio ? lesson.name : `${lesson.studio_name} / ${lesson.name}`;
                      return (
                        <option key={val} value={val}>
                          {label}
                        </option>
                      );
                    })}
                  </optgroup>
                );
              })}
            </select>
          </dd>
        </dl>
          </div>
        </div>
      </div>

      <div className="dataBoxWrap">
        <div className="dataBox">
          <div className="item">
            <h3>体験者数</h3>
            <p>{loading ? "0" : analyticsData.trialCount}</p>
          </div>
          <div className="item">
            <h3>体験数</h3>
            <p>{loading ? "0" : analyticsData.bookingCount}</p>
          </div>
          <div className="item">
            <h3>入会者数</h3>
            <p>{loading ? "0" : analyticsData.memberCount}</p>
          </div>
          <div className="item">
            <h3>入会率</h3>
            <p>{loading ? "0%" : `${analyticsData.conversionRate}%`}</p>
          </div>
        </div>

        <div className="dataBox graph">
          <div className="item">
            <h3>体験者と入会人数の推移</h3>
            {trendData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip contentStyle={contentStyle} labelStyle={labelStyle} />
                  <Legend />
                  <Line type="linear" dataKey="体験者" stroke={COLORS[0]} strokeWidth={2} name="体験者数" />
                  <Line type="linear" dataKey="入会者" stroke={COLORS[1]} strokeWidth={2} name="入会者数" />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ color: "#fff", padding: "20px", textAlign: "center" }}>
                データがありません
              </div>
            )}
          </div>

          <div className="item">
            <h3>来店のきっかけ</h3>
            {reasonData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={reasonData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {reasonData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={contentStyle} labelStyle={labelStyle} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ color: "#fff", padding: "20px", textAlign: "center" }}>
                データがありません
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
